import { useState } from 'react'
import { Tooltip, TooltipProps, Snackbar, SxProps, Theme } from '@mui/material'
import HistoryOutlined from '@mui/icons-material/HistoryOutlined'
import { useTranslation } from 'react-i18next'

export interface OriginalTextTooltipProps {
  /**
   * The original text to display in the tooltip and copy to clipboard
   */
  originalText: string
  /**
   * Optional custom message to show in the snackbar when text is copied
   */
  copiedMessage?: string
  /**
   * Optional custom styles for the icon
   */
  iconSx?: SxProps<Theme>
  /**
   * Optional tooltip placement
   * @default 'top'
   */
  placement?: TooltipProps['placement']
}

/**
 * A tooltip component that displays original text and allows copying it to clipboard
 */
export const OriginalTextTooltip = ({
  originalText,
  copiedMessage,
  iconSx,
  placement = 'top',
}: OriginalTextTooltipProps): JSX.Element => {
  const { t } = useTranslation()
  const [snackbarOpen, setSnackbarOpen] = useState(false)

  const handleCopyToClipboard = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigator.clipboard.writeText(originalText).then(() => {
      setSnackbarOpen(true)
    })
  }

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false)
  }

  return (
    <>
      <Tooltip
        title={originalText}
        placement={placement}
        arrow={true}
        disableInteractive={false}
        disableFocusListener={true}
        disableTouchListener={true}
        sx={{
          '& .MuiTooltip-tooltip': {
            backgroundColor: 'background.paper',
            color: 'text.primary',
            boxShadow: 1,
            border: 1,
            borderColor: 'divider',
            p: 2,
            userSelect: 'text',
          },
        }}
      >
        <HistoryOutlined
          color="secondary"
          fontSize="small"
          onClick={handleCopyToClipboard}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            cursor: 'pointer',
            ...iconSx,
          }}
        />
      </Tooltip>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={
          copiedMessage ||
          t(
            'dataValidation:visit-log.text-copied',
            'Original text copied to clipboard'
          )
        }
      />
    </>
  )
}
